import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApprovals } from '../hooks/useQueries';
import { useIsMobile } from '../hooks/useIsMobile';
import ListViewPage from '../components/Layout/ListViewPage';
import { formatDate } from '../utils/dateUtils';
import { formatItemRef } from '../utils/itemIdFormat';
import './approvals.css';

// Icon components for filters
const FunnelIcon = () => <i className="bi bi-funnel"></i>;
const PersonBadgeIcon = () => <i className="bi bi-person"></i>;

const normalizeMultiFilter = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value) return [value];
  return [];
};

export default function ApprovalsListView() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const listStateStorageKey = `approvals-list-state:${location.pathname}`;
  const initialListState = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(listStateStorageKey);
      if (!raw) return {};
      return JSON.parse(raw) || {};
    } catch (error) {
      console.warn('Failed to load approvals list state:', error);
      return {};
    }
  }, [listStateStorageKey]);

  const [searchTerm, setSearchTerm] = useState(initialListState.searchTerm || '');
  const [activeSortConfig, setActiveSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [completedSortConfig, setCompletedSortConfig] = useState({ key: 'updated_at', direction: 'desc' }); // Default to latest approved/denied
  const [statusFilter, setStatusFilter] = useState(() => normalizeMultiFilter(initialListState.statusFilter));
  const [roleFilter, setRoleFilter] = useState(() => normalizeMultiFilter(initialListState.roleFilter));
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    try {
      sessionStorage.setItem(
        listStateStorageKey,
        JSON.stringify({ searchTerm, statusFilter, roleFilter })
      );
    } catch (error) {
      console.warn('Failed to save approvals list state:', error);
    }
  }, [listStateStorageKey, searchTerm, statusFilter, roleFilter]);

  // React Query hooks
  const { data: approvals = [], isLoading: loading, error } = useApprovals();

  const handleCloseToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };

  // Split approvals into active (requested) and completed (approved/denied)
  const { activeApprovals, completedApprovals } = useMemo(() => {
    const active = approvals.filter(approval => approval.status === 'requested');
    const completed = approvals.filter(approval => ['approved', 'denied'].includes(approval.status));
    
    return { activeApprovals: active, completedApprovals: completed };
  }, [approvals]);

  // Get unique values for filter dropdowns
  const uniqueStatuses = useMemo(() => {
    const statuses = approvals.map(approval => approval.status).filter(Boolean);
    return [...new Set(statuses)].sort();
  }, [approvals]);

  const uniqueRoles = useMemo(() => {
    const roles = approvals.map(approval => approval.assigned_to_role_name).filter(Boolean);
    return [...new Set(roles)].sort();
  }, [approvals]);

  // Create options for react-select
  const statusOptions = useMemo(() => 
    uniqueStatuses.map(status => ({ value: status, label: status.charAt(0).toUpperCase() + status.slice(1) })), 
    [uniqueStatuses]
  );

  const roleOptions = useMemo(() => 
    uniqueRoles.map(role => ({ value: role, label: role })), 
    [uniqueRoles]
  );

  // Custom filtering logic
  const customFilter = (data) => {
    let filtered = data;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(approval =>
        approval.item_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        approval.stage_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        approval.assigned_to_role_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        approval.requested_by_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        approval.comments?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter.length > 0) {
      const allowedStatuses = new Set(statusFilter);
      filtered = filtered.filter(approval => allowedStatuses.has(approval.status));
    }

    // Apply role filter
    if (roleFilter.length > 0) {
      const allowedRoles = new Set(roleFilter);
      filtered = filtered.filter(approval => allowedRoles.has(approval.assigned_to_role_name));
    }

    return filtered;
  };

  // Row click handler
  const handleRowClick = (event, approval) => {
    const targetUrl = `/approvals/${approval.id}`;
    if (event?.button === 1 || event?.metaKey || event?.ctrlKey) {
      window.open(targetUrl, '_blank', 'noopener');
      return;
    }

    // Navigate to the approval detail page
    navigate(targetUrl);
  };

  // Get status badge class
  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return 'badge bg-success';
      case 'denied':
        return 'badge bg-danger';
      case 'requested':
        return 'badge bg-warning text-dark';
      default:
        return 'badge bg-secondary';
    }
  };

  // Column configuration for active approvals
  const activeColumns = useMemo(() => {
    const baseColumns = [
      { key: 'id', label: 'ID', width: isMobile ? '60px' : '80px', sortable: true },
      {
        key: 'item_title',
        label: 'Item',
        width: isMobile ? '150px' : '200px',
        exportValue: (approval) => {
          const id = formatItemRef(approval.item_id || approval.itemId || approval.item) || '#N/A';
          const title = approval.item_title || 'No title';
          return `${id} ${title}`;
        },
        render: (approval) => (
          <div>
            <strong>{formatItemRef(approval.item_id || approval.itemId || approval.item) || '#N/A'}</strong> {approval.item_title || 'No title'}
            {!(approval.item_id || approval.itemId || approval.item) && <small style={{color: 'red'}}> (Missing item_id)</small>}
          </div>
        )
      },
      { key: 'stage_name', label: 'Stage' },
      {
        key: 'assigned_to_role_name',
        label: 'Assigned To',
        exportValue: (approval) => {
          if (approval.assignment_type === 'user') {
            return approval.assigned_to_user_name || 'Unknown User';
          }
          return approval.assigned_to_role_name || 'Unknown Role';
        },
        render: (approval) => (
          <div>
            {approval.assignment_type === 'user' ? (
              <span>
                <i className="bi bi-person text-primary me-1"></i>
                {approval.assigned_to_user_name || 'Unknown User'}
              </span>
            ) : (
              <span>
                <i className="bi bi-shield text-warning me-1"></i>
                {approval.assigned_to_role_name || 'Unknown Role'}
              </span>
            )}
          </div>
        )
      },
      { key: 'requested_by_name', label: 'Requested By' },
      {
        key: 'comments',
        label: 'Request Comment',
        exportValue: (approval) => approval.comments || 'No comment',
        render: (approval) => (
          <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {approval.comments || 'No comment'}
          </div>
        )
      },
      {
        key: 'status',
        label: 'Status',
        exportValue: (approval) => {
          const status = approval.status || '';
          return status ? status.charAt(0).toUpperCase() + status.slice(1) : '-';
        },
        render: (approval) => (
          <span className={getStatusBadge(approval.status)}>
            {approval.status.charAt(0).toUpperCase() + approval.status.slice(1)}
          </span>
        )
      },
      { key: 'created_at', label: 'Created', exportValue: (approval) => approval.created_at ? formatDate(approval.created_at) : '-', render: (approval) => formatDate(approval.created_at) }
    ];

    // On mobile, hide less critical columns
    if (isMobile) {
      return baseColumns.filter(col => ['id', 'item_title', 'stage_name', 'status'].includes(col.key));
    }

    return baseColumns;
  }, [isMobile]);

  // Column configuration for completed approvals (no actions, no created date)
  const completedColumns = [
    {
      key: 'id',
      label: 'ID',
      width: '60px', // Smaller width for completed section
      sortable: true
    },
    {
      key: 'item_title',
      label: 'Item Title',
      width: '200px',
      exportValue: (approval) => {
        const id = formatItemRef(approval.item_id || approval.itemId || approval.item) || '#N/A';
        const title = approval.item_title || 'No title';
        return `${id} ${title}`;
      },
      render: (approval) => (
        <div>
          <strong>{formatItemRef(approval.item_id || approval.itemId || approval.item) || '#N/A'}</strong> {approval.item_title || 'No title'}
          {!(approval.item_id || approval.itemId || approval.item) && <small style={{color: 'red'}}> (Missing item_id)</small>}
        </div>
      )
    },
    {
      key: 'stage_name',
      label: 'Stage'
    },
    {
      key: 'assigned_to_role_name',
      label: 'Assigned To',
      exportValue: (approval) => {
        if (approval.assignment_type === 'user') {
          return approval.assigned_to_user_name || 'Unknown User';
        }
        return approval.assigned_to_role_name || 'Unknown Role';
      },
      render: (approval) => (
        <div>
          {approval.assignment_type === 'user' ? (
            <span>
              <i className="bi bi-person text-primary me-1"></i>
              {approval.assigned_to_user_name || 'Unknown User'}
            </span>
          ) : (
            <span>
              <i className="bi bi-shield text-warning me-1"></i>
              {approval.assigned_to_role_name || 'Unknown Role'}
            </span>
          )}
        </div>
      )
    },
    {
      key: 'requested_by_name',
      label: 'Requested By'
    },
    {
      key: 'comments',
      label: 'Request Comment',
      exportValue: (approval) => approval.comments || 'No comment',
      render: (approval) => (
        <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {approval.comments || 'No comment'}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      exportValue: (approval) => {
        const status = approval.status || '';
        return status ? status.charAt(0).toUpperCase() + status.slice(1) : '-';
      },
      render: (approval) => (
        <span className={getStatusBadge(approval.status)}>
          {approval.status.charAt(0).toUpperCase() + approval.status.slice(1)}
        </span>
      )
    },
    {
      key: 'updated_at',
      label: 'Responded Date',
      exportValue: (approval) => approval.updated_at ? formatDate(approval.updated_at) : '-',
      render: (approval) => approval.updated_at ? formatDate(approval.updated_at) : '-',
      sortable: true
    }
  ];

  // Filter options for SearchFilters component
  const filters = [
    {
      icon: FunnelIcon,
      value: statusOptions.filter(option => statusFilter.includes(option.value)),
      onChange: (selectedOptions) => setStatusFilter(Array.isArray(selectedOptions) ? selectedOptions.map(opt => opt.value) : []),
      options: statusOptions,
      placeholder: 'All statuses',
      isClearable: true,
      isMulti: true
    },
    {
      icon: PersonBadgeIcon,
      value: roleOptions.filter(option => roleFilter.includes(option.value)),
      onChange: (selectedOptions) => setRoleFilter(Array.isArray(selectedOptions) ? selectedOptions.map(opt => opt.value) : []),
      options: roleOptions,
      placeholder: 'All roles',
      isClearable: true,
      isMulti: true
    }
  ];

  return (
    <ListViewPage
      // Page configuration
      title="Approvals"
      itemLabel="approvals"
      tableName="approvals"
      
      // Data and loading
      activeData={activeApprovals}
      completedData={completedApprovals}
      loading={loading}
      error={error}
      
      // Columns - use different columns for active vs completed
      columns={activeColumns}
      completedColumns={completedColumns}
      defaultVisibleColumns={['id', 'item_title', 'stage_name', 'assigned_to_role_name', 'requested_by_name', 'comments', 'status', 'created_at', 'updated_at']}
      
      // Search and filtering
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder="Search approvals..."
      filters={filters}
      
      // Sorting
      sortConfig={activeSortConfig}
      onSort={setActiveSortConfig}
      completedSortConfig={completedSortConfig}
      onCompletedSort={setCompletedSortConfig}
      
      // Row handling
      onRowClick={handleRowClick}
      
      // Split view configuration
      completedSectionTitle="Completed Approvals"
      completedSectionIcon="bi-check-circle"
      
      // Custom filtering
      customFilter={customFilter}
      
      // Export functionality
      enableExport={true}
      exportFileName="approvals_export"
      
      // Toast notifications
      toast={toast}
      onCloseToast={handleCloseToast}
    />
  );
}